import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Loader2 } from "lucide-react";

import AccountSearchFilter from "./AccountSearchFilter";
import {
  dateTime,
  displayState,
  eventLine,
  lastActivity,
  shortDate,
  STATE_LABEL,
  STATE_ORDER,
  stateReason,
  type EventLine,
  type HistoryRecord,
  type HistoryState,
} from "./actionHistoryFormat";
import Pagination from "../../Components/Pagination";
import { useAppDispatch } from "../../Store/Hooks";
import type { SignalTier } from "../../Store/Reducers/SignalFeedSlice";
import { fetchMyActionHistory } from "../../Store/Reducers/SignalHistorySlice";

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const DEFAULT_PAGE_SIZE = 10;

/**
 * My Accounts → Actions → History (V1): one row per signal on the user's
 * accounts, newest activity first. Each row names the signal and account,
 * shows the spec state (Open / Handled / Snoozed / Resolved) with a short
 * reason, and a plain-words trail of who did what and when. Filters:
 * account (searchable), state, tier.
 */

const TIER_META: Record<SignalTier, { label: string; className: string }> = {
  critical: { label: "Critical", className: "bg-red-50 text-red-700" },
  warning: { label: "Warning", className: "bg-amber-50 text-amber-700" },
  opp: { label: "Opportunity", className: "bg-emerald-50 text-emerald-700" },
  maint: { label: "Maintenance", className: "bg-slate-100 text-slate-600" },
};

const STATE_CLASS: Record<HistoryState, string> = {
  active: "bg-amber-50 text-amber-700",
  acknowledged: "bg-blue-50 text-blue-700",
  snoozed: "bg-slate-100 text-slate-600",
  resolved: "bg-emerald-50 text-emerald-700",
};

const selectClass =
  "h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-[12.5px] text-gray-800 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20";

/** Trails longer than this show the first step, a "show N more" button and the last few. */
const TRAIL_LIMIT = 6;
const TRAIL_TAIL = 4;

const Step: React.FC<{ line: EventLine }> = ({ line }) => (
  <span className="text-gray-500">
    {line.who && (
      <>
        <span className="font-semibold text-gray-900">{line.who}</span>
        {line.role && <span> ({line.role})</span>}{" "}
      </>
    )}
    {line.who ? (
      line.verb
    ) : (
      <span
        className={
          line.verb === "Appeared" ? "" : "font-semibold text-gray-900"
        }
      >
        {line.verb}
      </span>
    )}
    {line.object && (
      <>
        {" "}
        <span className="font-semibold text-gray-900">{line.object}</span>
      </>
    )}
    {line.at && (
      <time
        dateTime={line.at}
        title={dateTime(line.at)}
        className="ml-1 text-gray-400"
      >
        {shortDate(line.at)}
      </time>
    )}
  </span>
);

const Arrow: React.FC = () => (
  <span className="text-gray-300" aria-hidden>
    →
  </span>
);

const Trail: React.FC<{ lines: EventLine[] }> = ({ lines }) => {
  const [all, setAll] = useState(false);
  const hidden = lines.length > TRAIL_LIMIT && !all;
  const shown = hidden
    ? [lines[0], ...lines.slice(lines.length - TRAIL_TAIL)]
    : lines;
  const skipped = lines.length - shown.length;
  return (
    <ol
      aria-label="What happened"
      className="m-0 mt-1.5 flex list-none flex-wrap items-center gap-x-1.5 gap-y-1 p-0 text-[12.5px]"
    >
      {shown.map((line, i) => (
        <React.Fragment key={`${line.verb}-${line.at}-${i}`}>
          {hidden && i === 1 && (
            <li className="flex items-center gap-1.5">
              <Arrow />
              <button
                type="button"
                onClick={() => setAll(true)}
                className="rounded bg-gray-100 px-1.5 py-0.5 text-[11.5px] font-semibold text-gray-600 hover:bg-gray-200"
              >
                {skipped} more step{skipped === 1 ? "" : "s"}
              </button>
            </li>
          )}
          <li className="flex items-center gap-1.5">
            {i > 0 && <Arrow />}
            <Step line={line} />
          </li>
        </React.Fragment>
      ))}
    </ol>
  );
};

const HistoryRow: React.FC<{ record: HistoryRecord }> = ({ record }) => {
  const state = displayState(record);
  const reason = stateReason(record);
  const last = lastActivity(record);
  const lines = record.events
    .map(eventLine)
    .filter((l): l is EventLine => l !== null);
  const tier = TIER_META[record.tier];
  return (
    <li
      data-testid="history-row"
      data-firing-id={record.firing_id}
      className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 border-t border-gray-100 px-5 py-3.5 first:border-t-0"
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span
            className={`rounded px-1.5 py-0.5 text-[10.5px] font-bold uppercase tracking-wide ${
              tier?.className ?? ""
            }`}
          >
            {tier?.label ?? record.tier}
          </span>
          <span className="text-[13.5px] font-bold text-gray-900">
            {record.title}
          </span>
        </div>
        <div className="mt-0.5 text-[12.5px] font-semibold text-brand">
          {record.account_name}
        </div>
      </div>
      <div className="row-span-2 flex flex-col items-end gap-0.5 text-right">
        <span
          data-testid="history-state"
          className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${STATE_CLASS[state]}`}
        >
          {STATE_LABEL[state]}
        </span>
        {reason && (
          <span className="whitespace-nowrap text-[12px] text-gray-500">
            {reason}
          </span>
        )}
        {last && (
          <time
            dateTime={last}
            title="Last activity"
            className="whitespace-nowrap text-[12px] tabular-nums text-gray-400"
          >
            {dateTime(last)}
          </time>
        )}
      </div>
      <div className="col-span-1 min-w-0">
        <Trail lines={lines} />
      </div>
    </li>
  );
};

const MyAccountsActionHistory: React.FC = () => {
  const dispatch = useAppDispatch();
  const [records, setRecords] = useState<HistoryRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [accountQuery, setAccountQuery] = useState("");
  const [stateFilter, setStateFilter] = useState<HistoryState | "">("");
  const [tierFilter, setTierFilter] = useState<SignalTier | "">("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await dispatch(fetchMyActionHistory()).unwrap();
      const content = (res as { content?: { records?: HistoryRecord[] } })
        ?.content;
      setRecords(content?.records ?? []);
    } catch (e: unknown) {
      const message =
        typeof e === "string" ? e : "Couldn't load action history.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    void load();
  }, [load]);

  const sorted = useMemo(
    () =>
      [...records].sort((a, b) =>
        (lastActivity(b) ?? "").localeCompare(lastActivity(a) ?? "")
      ),
    [records]
  );

  const accountOptions = useMemo(() => {
    const counts = new Map<string, number>();
    records.forEach(r =>
      counts.set(r.account_name, (counts.get(r.account_name) ?? 0) + 1)
    );
    return Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, n]) => ({
        name,
        hint: `${n} signal${n === 1 ? "" : "s"}`,
      }));
  }, [records]);

  const tiers = useMemo(
    () =>
      (Object.keys(TIER_META) as SignalTier[]).filter(t =>
        records.some(r => r.tier === t)
      ),
    [records]
  );

  const query = accountQuery.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      sorted.filter(
        r =>
          (!query || r.account_name.toLowerCase().includes(query)) &&
          (!stateFilter || displayState(r) === stateFilter) &&
          (!tierFilter || r.tier === tierFilter)
      ),
    [sorted, query, stateFilter, tierFilter]
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const shown = filtered.slice(pageStart, pageStart + pageSize);
  const filtering = !!(query || stateFilter || tierFilter);
  const clearFilters = () => {
    setAccountQuery("");
    setStateFilter("");
    setTierFilter("");
    setPage(1);
  };
  const setAccountFilter = (value: string) => {
    setAccountQuery(value);
    setPage(1);
  };
  const setStateFilterAndReset = (value: HistoryState | "") => {
    setStateFilter(value);
    setPage(1);
  };
  const setTierFilterAndReset = (value: SignalTier | "") => {
    setTierFilter(value);
    setPage(1);
  };

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-[15px] font-bold text-gray-900">Action History</h3>

      <div className="flex flex-wrap items-center gap-2">
        <AccountSearchFilter
          value={accountQuery}
          onChange={setAccountFilter}
          options={accountOptions}
        />
        <select
          aria-label="State"
          value={stateFilter}
          onChange={e =>
            setStateFilterAndReset(e.target.value as HistoryState | "")
          }
          className={selectClass}
        >
          <option value="">All states</option>
          {STATE_ORDER.map(s => (
            <option key={s} value={s}>
              {STATE_LABEL[s]}
            </option>
          ))}
        </select>
        <select
          aria-label="Tier"
          value={tierFilter}
          onChange={e =>
            setTierFilterAndReset(e.target.value as SignalTier | "")
          }
          className={selectClass}
        >
          <option value="">All tiers</option>
          {tiers.map(t => (
            <option key={t} value={t}>
              {TIER_META[t].label}
            </option>
          ))}
        </select>
        {filtering && (
          <button
            type="button"
            onClick={clearFilters}
            className="text-[12.5px] font-semibold text-brand hover:underline"
          >
            Clear filters
          </button>
        )}
        <span className="ml-auto text-xs text-gray-500" aria-live="polite">
          {loading
            ? "Loading…"
            : `${filtered.length} signal${filtered.length === 1 ? "" : "s"}`}
        </span>
      </div>

      {error && (
        <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => void load()}
            className="font-semibold underline"
          >
            Retry
          </button>
        </div>
      )}

      <section className="rounded-xl border border-gray-200 bg-white">
        {loading && records.length === 0 ? (
          <div className="flex items-center justify-center gap-2 py-8 text-[12.5px] text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Loading history…
          </div>
        ) : records.length === 0 ? (
          <p className="py-8 text-center text-[12.5px] text-gray-500">
            No signal activity on your accounts yet.
          </p>
        ) : filtered.length === 0 ? (
          <p className="py-8 text-center text-[12.5px] text-gray-500">
            Nothing matches these filters.{" "}
            <button
              type="button"
              onClick={clearFilters}
              className="font-semibold text-brand hover:underline"
            >
              Clear filters
            </button>
          </p>
        ) : (
          <ol aria-label="Action history" className="m-0 list-none p-0">
            {shown.map(r => (
              <HistoryRow key={r.firing_id} record={r} />
            ))}
          </ol>
        )}
      </section>

      {filtered.length > 0 && (
        <Pagination
          mode="static"
          dataLength={filtered.length}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={size => {
            setPageSize(size);
            setPage(1);
          }}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          showPageSizeSelector
        />
      )}
    </div>
  );
};

export default MyAccountsActionHistory;
